import { Modal as AntDesignModal, Typography } from 'antd';
import cx from 'classnames';
import React, {
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import Draggable, { DraggableProps } from 'react-draggable';
import { MdDragHandle, MdOutlineCancel } from 'react-icons/md';

import Button from '../button/Button';
import styles from './_Modal.module.scss';

const DEFAULT_POSITION = { x: 0, y: 0 };

const DraggableComponent = Draggable as unknown as React.ComponentType<
  Partial<DraggableProps>
>;

type ModalProps = {
  title?: string;
  isOpen?: boolean;
  mask?: boolean;
  draggable?: boolean;
  onHandleOk?: () => void;
  onHandleCancel?: () => void;
  onHandleModalIconCancel?: () => void;
  renderModalContent?: () => null | ReactNode;
  cancelLabel?: string;
  okLabel?: string;
  footerIconPosition?: 'start' | 'end';
  cancelIcon?: React.ElementType;
  okIcon?: React.ElementType;
  renderFooter?: boolean;
  zIndex?: number;
  maskClosable?: boolean;
  okButtonType?:
    | 'primary'
    | 'outline'
    | 'destructive'
    | 'link'
    | 'linkNeutral'
    | 'outlineNeutral';
  hideCancelButton?: boolean;
  hideCloseButton?: boolean;
  wrapClassName?: string;
  showDefaultCloseButton?: boolean;
  okLoading?: boolean;
  okDisabled?: boolean;
  okDisabledTooltip?: string;
  cancelDisabled?: boolean;
  destroyOnHide?: boolean;
  allowInteractionBehind?: boolean;
  modalClassName?: string;
  defaultPosition?: { x: number; y: number };
};

const { Text } = Typography;

const Modal: React.FC<ModalProps> = ({
  title,
  isOpen,
  onHandleOk,
  onHandleModalIconCancel,
  onHandleCancel,
  draggable,
  renderModalContent,
  cancelLabel,
  cancelIcon,
  okLabel,
  okIcon,
  mask,
  footerIconPosition = 'start',
  renderFooter = true,
  okButtonType = 'primary',
  zIndex,
  maskClosable = true,
  hideCancelButton = false,
  hideCloseButton = false,
  wrapClassName,
  showDefaultCloseButton = false,
  okLoading = false,
  okDisabled = false,
  okDisabledTooltip,
  cancelDisabled = false,
  destroyOnHide = false,
  allowInteractionBehind = false,
  modalClassName,
  defaultPosition,
}) => {
  const dragRef = useRef(null);
  const [bounds, setBounds] = useState({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  });

  const onStart = (_: any, data: { x: number; y: number }) => {
    const { clientWidth, clientHeight } = document.documentElement;
    const modal = dragRef.current;

    if (modal) {
      const rect = (modal as any).getBoundingClientRect();

      setBounds({
        left: -rect.left + data.x,
        right: clientWidth - rect.right + data.x,
        top: -rect.top + data.y,
        bottom: clientHeight - rect.bottom + data.y,
      });
    }
  };

  const renderDragIndicator = useMemo(
    () => (
      <div className="tw-absolute custom-drag-handle tw-w-full tw-flex tw-items-center tw-justify-center tw-z-[9999] tw-pointer-events-auto tw-select-none">
        <MdDragHandle
          className={cx(
            styles.dragIconClass,
            'tw-w-10 tw-h-3 tw-cursor-move tw--translate-y-1/2 tw-bg-white tw-rounded',
          )}
          size={12}
        />
      </div>
    ),
    [],
  );
  const renderModal = useCallback(
    (modal: any) => (
      <DraggableComponent
        handle=".custom-drag-handle"
        bounds={bounds}
        onStart={onStart}
        disabled={!draggable}
        defaultPosition={defaultPosition || DEFAULT_POSITION}
      >
        <div
          ref={dragRef}
          style={{ position: 'relative' }}
          className={styles.modalClass}
        >
          {draggable && renderDragIndicator}
          {modal}
        </div>
      </DraggableComponent>
    ),
    [bounds, defaultPosition, draggable, renderDragIndicator],
  );

  const renderFooterContent = useCallback(
    () => (
      <div className="tw-flex tw-justify-end tw-gap-2">
        {!hideCancelButton && (
          <Button
            onClick={onHandleCancel}
            type="outline"
            label={cancelLabel}
            iconPosition={footerIconPosition}
            ReactIconClass={cancelIcon}
            disabled={cancelDisabled}
          />
        )}
        <Button
          type={okButtonType}
          onClick={onHandleOk}
          label={okLabel}
          iconPosition={footerIconPosition}
          ReactIconClass={okIcon}
          loading={okLoading}
          disabled={okDisabled}
          tooltipText={okDisabled ? okDisabledTooltip : undefined}
        />
      </div>
    ),
    [
      hideCancelButton,
      onHandleCancel,
      cancelLabel,
      footerIconPosition,
      cancelIcon,
      okButtonType,
      onHandleOk,
      okLabel,
      okIcon,
      okLoading,
      okDisabled,
      okDisabledTooltip,
      cancelDisabled,
    ],
  );

  const onModalIconCancel =
    onHandleModalIconCancel === undefined
      ? onHandleCancel
      : onHandleModalIconCancel;

  return (
    <AntDesignModal
      open={isOpen}
      onCancel={onHandleCancel}
      footer={null}
      closeIcon={showDefaultCloseButton ? <MdOutlineCancel /> : null}
      modalRender={renderModal}
      width="auto"
      centered
      mask={mask === false ? false : { closable: maskClosable }}
      zIndex={zIndex}
      wrapClassName={cx(wrapClassName, {
        [styles.modalNoInteractionBlock]: allowInteractionBehind,
      })}
      style={allowInteractionBehind ? { pointerEvents: 'none' } : {}}
      destroyOnHidden={destroyOnHide}
      className={modalClassName}
    >
      <div className="tw-flex tw-flex-col tw-gap-3">
        <div className="tw-flex tw-justify-between tw-items-center">
          <Text className="tw-text-primary tw-font-semibold">{title}</Text>
          {!hideCloseButton && (
            <MdOutlineCancel
              className="tw-cursor-pointer"
              size={16}
              onClick={onModalIconCancel}
            />
          )}
        </div>
        {renderModalContent && renderModalContent()}
        {renderFooter && renderFooterContent()}
      </div>
    </AntDesignModal>
  );
};

export default Modal;
